import { confirmsFileOffer, detectIntent } from "./intent.js";

/** What a message needs, judged from its text alone (no model call). */
export interface RequestClass {
  /** fast: quick chat · balanced: normal · power: hard / long / code / analysis. */
  level: "fast" | "balanced" | "power";
  /** The request needs tools (documents, connected accounts, running code, research): agent mode. */
  agent: boolean;
  /** Needs a model that can see images. */
  vision: boolean;
  /** One short phrase saying why (shown to the user). */
  reason: string;
}

const CODE =
  /```|\b(function|class|def|import|SELECT|JOIN|regex|stack ?trace|exception|debug|refactor|algorithm|compile|api|sql|python|javascript|typescript|bash)\b|(?:كود|برمج|بايثون|دالة|اكتب برنامج|خطأ برمجي)/i;
const HARD =
  /\b(analy[sz]e|analysis|compare|strategy|architecture|prove|derive|forecast|optimi[sz]e|trade-?offs?|in detail|step[- ]by[- ]step|business plan|roadmap|erlang|staffing|shrinkage|statistics|regression|equation|calculate|simulate|root cause|review|audit|essay|report|research)\b|(?:حلل|تحليل|قارن|مقارنة|استراتيجي|خطة|بالتفصيل|خطوة خطوة|معادلة|احسب|توقع|حسّن|حسن|تقرير|مقال|بحث|راجع|مراجعة|اشرح لي بعمق)/i;
const RUN_CODE =
  /\b(run|execute|use)\s+(python|code|a script|shell|bash)\b|(?:شغل|نفذ|استخدم)\s*(?:لي\s*)?(?:بايثون|python|كود|سكريبت)/i;
const RESEARCH =
  /\b(research|deep dive|find sources|look up and (compare|summari[sz]e))\b|(?:ابحث (?:لي )?(?:و|عن)|دوّر|دور على)/i;
const FRESH =
  /(?:آخر|اخر|أخبار|اخبار|خبر|تطورات|النهارد[هة]|اليوم|دلوقتي|حالي[اًا]|الآن|الان|امبارح|أمس|حصل ايه|حصل اي|عاجل|سعر|اسعار|أسعار|نتيج[هة]|مباراة|ماتش|\b(?:latest|today|tonight|news|breaking|updates?|yesterday|this (?:week|month)|price of|score|who won|election|stock)\b)/i;
const GREETING =
  /^\s*(hi|hello|hey|thanks?|thank you|ok|okay|good (morning|evening)|مرحبا|اهلا|أهلا|سلام|شكرا|شكراً|تمام|صباح الخير|مساء الخير|ازيك|إزيك)\b/i;

export function classifyRequest(
  text: string,
  opts: { hasImages?: boolean; lastAssistant?: string } = {},
): RequestClass {
  const t = text.trim();
  const intent = detectIntent(t);
  const agent =
    intent === "agent" ||
    RUN_CODE.test(t) ||
    RESEARCH.test(t) ||
    confirmsFileOffer(t, opts.lastAssistant ?? "");
  const vision = !!opts.hasImages;
  if (agent) return { level: "power", agent: true, vision, reason: "tools" };
  if (vision) return { level: "balanced", agent: false, vision, reason: "vision" };
  // News / "today" questions are answered from live search results: needs a strong model that follows sources faithfully.
  if (FRESH.test(t) && t.split(/\s+/).length >= 3)
    return { level: "balanced", agent: false, vision, reason: "fresh" };
  if (CODE.test(t)) return { level: "power", agent: false, vision, reason: "code" };
  if (t.length > 700) return { level: "power", agent: false, vision, reason: "long" };
  if (HARD.test(t)) return { level: "power", agent: false, vision, reason: "hard" };
  if (GREETING.test(t) || (t.length <= 80 && !t.includes("\n")))
    return { level: "fast", agent: false, vision, reason: "quick" };
  return { level: "balanced", agent: false, vision, reason: "normal" };
}

export interface AutoCandidate {
  providerId: string;
  model: string;
  /** 0–100, higher = more capable. */
  strength: number;
  /** 1–5, higher = faster. */
  speed: number;
  cost: "free" | "quota" | "paid";
  capabilities: { tools?: boolean; vision?: boolean; thinking?: boolean };
}

/** Score for how well a model fits the request level; higher is better. Quality first: speed only breaks ties for normal requests. */
function fit<T extends AutoCandidate>(m: T, c: RequestClass): number {
  const paidPenalty = m.cost === "paid" ? 25 : 0; // free-first: only reach for paid when nothing free fits
  const slowPenalty = m.speed <= 1 ? 12 : 0; // long hidden reasoning before any answer
  if (c.level === "power")
    return (
      m.strength + (c.reason !== "tools" && m.capabilities.thinking ? 3 : 0) + m.speed - paidPenalty
    );
  if (c.level === "fast") return m.speed * 14 + m.strength * 0.5 - slowPenalty - paidPenalty;
  return m.strength + m.speed * 2 - slowPenalty - paidPenalty;
}

/** Ranked list (best fit first) of the models that can do the job, or [] when none can. */
export function rankForRequest<T extends AutoCandidate>(
  models: T[],
  c: RequestClass,
  opts: { keepPaid?: boolean } = {},
): T[] {
  let pool = models.filter(
    (m) => (!c.agent || m.capabilities.tools) && (!c.vision || m.capabilities.vision),
  );
  // Quick answers still need a model that can be trusted: never go below a quality floor when better ones exist.
  const floor = c.level === "fast" ? 65 : c.level === "balanced" ? 70 : 0;
  if (floor && pool.some((m) => m.strength >= floor))
    pool = pool.filter((m) => m.strength >= floor);
  // A free option beats a paid one whenever it can do the job.
  if (!opts.keepPaid && pool.some((m) => m.cost !== "paid"))
    pool = pool.filter((m) => m.cost !== "paid");
  return pool
    .map((m, i) => ({ m, i, s: fit(m, c) }))
    .sort((a, b) => b.s - a.s || a.i - b.i)
    .map((e) => e.m);
}
